import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, Search, Heart, Trash2, FileText, Plus, BadgeCheck } from "lucide-react";
import DocumentVault from "../../components/DocumentVault";

const POPULATIONS = ["veterans", "seniors", "men", "women", "couples", "families", "recovery", "reentry", "disabled", "general"];
const DOC_TYPES = [
  { value: "gov_id", label: "Government ID" },
  { value: "income_proof", label: "Proof of Income" },
  { value: "paystub", label: "Pay Stub" },
  { value: "employment_verification", label: "Employment Verification" },
  { value: "benefit_letter", label: "Benefit Letter" },
  { value: "veteran_doc", label: "Veteran Documentation" },
  { value: "referral_letter", label: "Referral Letter" },
  { value: "housing_doc", label: "Housing Document" },
  { value: "other", label: "Other" },
];

export default function SeekerDashboard() {
  const { user } = useAuth();
  const [intake, setIntake] = useState(null);
  const [applications, setApplications] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docOpen, setDocOpen] = useState(false);
  const [docForm, setDocForm] = useState({ name: "", document_type: "gov_id", file: null, notes: "", expires_at: "", linked_to: "", linked_id: "" });
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    veteran_status: false,
    branch_of_service: "",
    current_situation: "",
    urgency: "standard",
    preferred_locations: [],
    preferred_state: "",
    preferred_county: "",
    preferred_city: "",
    populations: [],
    housing_needs: [],
    notes: "",
    income_source: "",
    has_id: false,
    contact_phone: user?.phone || "",
  });
  const [locInput, setLocInput] = useState("");
  const [needsInput, setNeedsInput] = useState("");

  const load = useCallback(async () => {
    const [i, a, f, d] = await Promise.all([
      api.get("/intake/me").catch(() => ({ data: null })),
      api.get("/applications/me").catch(() => ({ data: [] })),
      api.get("/favorites").catch(() => ({ data: [] })),
      api.get("/documents/me").catch(() => ({ data: [] })),
    ]);
    if (i.data) { setIntake(i.data); setForm((p) => ({ ...p, ...i.data })); }
    setApplications(a.data);
    setFavorites(f.data);
    setDocuments(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadDoc = async () => {
    if (!docForm.name || !docForm.file) { toast.error("Name and file are required"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", docForm.file);
      fd.append("name", docForm.name);
      fd.append("document_type", docForm.document_type);
      if (docForm.notes) fd.append("notes", docForm.notes);
      if (docForm.expires_at) fd.append("expires_at", docForm.expires_at);
      if (docForm.linked_to) fd.append("linked_to", docForm.linked_to);
      if (docForm.linked_id) fd.append("linked_id", docForm.linked_id);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — pending admin review");
      setDocOpen(false);
      setDocForm({ name: "", document_type: "gov_id", file: null, notes: "", expires_at: "", linked_to: "", linked_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload");
    } finally { setUploading(false); }
  };
  const removeDoc = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };
  const togglePop = (p) => setForm({ ...form, populations: form.populations.includes(p) ? form.populations.filter(x => x !== p) : [...form.populations, p] });

  const submit = async () => {
    try {
      const { data } = await api.post("/intake", form);
      setIntake(data);
      toast.success("Intake submitted — providers and case managers can now match you.");
    } catch { toast.error("Could not submit intake"); }
  };

  const removeFav = async (id) => {
    await api.delete(`/favorites/${id}`);
    setFavorites(favorites.filter((f) => f.id !== id));
    toast.success("Removed");
  };

  return (
    <div data-testid="seeker-dashboard" className="max-w-6xl mx-auto px-6 sm:px-8 py-10">
      <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-2">Housing Seeker</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Welcome, {user.full_name.split(" ")[0]}.</h1>

      {intake?.verified_applicant && (
        <div className="mt-6 bg-[#C6F6D5] border-l-4 border-[#276749] p-4 flex items-center gap-3 rounded-sm">
          <CheckCircle2 className="w-5 h-5 text-[#276749]" />
          <div>
            <div className="font-semibold text-[#22543D] text-sm">You are a Verified Applicant.</div>
            <div className="text-xs text-[#22543D]/80">Providers will see your verified badge with every referral.</div>
          </div>
        </div>
      )}

      {intake?.verifications && Object.keys(intake.verifications).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(intake.verifications).filter(([_, v]) => v).map(([k]) => (
            <Badge key={k} className="bg-[#C6F6D5] text-[#22543D] capitalize" data-testid={`verif-${k}`}><BadgeCheck className="w-3 h-3 mr-1" /> {k} Verified</Badge>
          ))}
        </div>
      )}

      <Tabs defaultValue="intake" className="mt-8">
        <TabsList>
          <TabsTrigger value="intake" data-testid="tab-intake">My Intake</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications">Applications ({applications.length})</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites">Saved ({favorites.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Document Vault ({documents.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="intake">
          {intake && (
            <div className="mb-4 bg-white border-l-4 border-[#276749] p-3 text-sm text-[#0A192F]">Intake on file. Update anytime.</div>
          )}
          <div className="bg-white border border-[#D1D5DB] p-7 rounded-sm">
            <h2 className="font-heading text-xl font-semibold text-[#0A192F]">{intake ? "Update your intake" : "Complete your intake"}</h2>
            <p className="text-sm text-[#4A5568] mt-1">Honest information helps us match you faster — Safe Harbor never charges seekers.</p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name"><Input data-testid="intake-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
              <Field label="Contact phone"><Input data-testid="intake-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Field>
              <Field label="Urgency">
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger data-testid="intake-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent — no place to sleep tonight</SelectItem>
                    <SelectItem value="standard">Standard — need within 30 days</SelectItem>
                    <SelectItem value="planning">Planning — looking ahead</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Income source"><Input data-testid="intake-income" value={form.income_source} onChange={(e) => setForm({ ...form, income_source: e.target.value })} placeholder="VA benefits, SSI, employment, none…" /></Field>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid="intake-vet" checked={form.veteran_status} onCheckedChange={(v) => setForm({ ...form, veteran_status: !!v })} />
                  I am a veteran
                </label>
              </div>
              {form.veteran_status && (
                <Field label="Branch of service"><Input data-testid="intake-branch" value={form.branch_of_service} onChange={(e) => setForm({ ...form, branch_of_service: e.target.value })} placeholder="Army, Navy, Marines…" /></Field>
              )}
              <Field label="Have government ID?">
                <label className="flex items-center gap-2 text-sm h-10">
                  <Checkbox data-testid="intake-id" checked={form.has_id} onCheckedChange={(v) => setForm({ ...form, has_id: !!v })} /> Yes
                </label>
              </Field>
              <div className="md:col-span-2">
                <Label>Current situation</Label>
                <Textarea data-testid="intake-situation" rows={3} value={form.current_situation} onChange={(e) => setForm({ ...form, current_situation: e.target.value })} placeholder="Briefly describe where you are now…" />
              </div>
              <Field label="Preferred state"><Input data-testid="intake-pref-state" value={form.preferred_state} onChange={(e) => setForm({ ...form, preferred_state: e.target.value.toUpperCase() })} maxLength={2} placeholder="NC" /></Field>
              <Field label="Preferred county"><Input data-testid="intake-pref-county" value={form.preferred_county} onChange={(e) => setForm({ ...form, preferred_county: e.target.value })} placeholder="Mecklenburg" /></Field>
              <Field label="Preferred city"><Input data-testid="intake-pref-city" value={form.preferred_city} onChange={(e) => setForm({ ...form, preferred_city: e.target.value })} placeholder="Charlotte" /></Field>
              <div className="md:col-span-2">
                <Label>Populations / programs that fit me</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-2">
                  {POPULATIONS.map(p => (
                    <label key={p} className="flex items-center gap-2 text-xs capitalize">
                      <Checkbox data-testid={`intake-pop-${p}`} checked={form.populations.includes(p)} onCheckedChange={() => togglePop(p)} />
                      {p}
                    </label>
                  ))}
                </div>
              </div>
              <Field label="Other preferred locations (press Enter)">
                <Input value={locInput} onChange={(e) => setLocInput(e.target.value)} data-testid="intake-loc-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && locInput.trim()) { e.preventDefault(); setForm({ ...form, preferred_locations: [...form.preferred_locations, locInput.trim()] }); setLocInput(""); } }}
                  placeholder="Greensboro, NC" />
                <div className="flex flex-wrap gap-1.5 mt-2">{form.preferred_locations.map((l, i) => <Badge key={`loc-${l}-${i}`} variant="outline" className="cursor-pointer" onClick={() => setForm({ ...form, preferred_locations: form.preferred_locations.filter((_, idx) => idx !== i) })}>{l} ×</Badge>)}</div>
              </Field>
              <Field label="Housing needs (press Enter)">
                <Input value={needsInput} onChange={(e) => setNeedsInput(e.target.value)} data-testid="intake-needs-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && needsInput.trim()) { e.preventDefault(); setForm({ ...form, housing_needs: [...form.housing_needs, needsInput.trim()] }); setNeedsInput(""); } }}
                  placeholder="ADA, sober, pet-friendly…" />
                <div className="flex flex-wrap gap-1.5 mt-2">{form.housing_needs.map((l, i) => <Badge key={`need-${l}-${i}`} variant="outline" className="cursor-pointer" onClick={() => setForm({ ...form, housing_needs: form.housing_needs.filter((_, idx) => idx !== i) })}>{l} ×</Badge>)}</div>
              </Field>
              <div className="md:col-span-2">
                <Label>Notes for case managers</Label>
                <Textarea data-testid="intake-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="mt-7 flex justify-end">
              <Button data-testid="intake-submit" onClick={submit} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white px-7">
                {intake ? "Update Intake" : "Submit Intake"}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="applications">
          {applications.length === 0 ? (
            <div className="border border-dashed border-[#D1D5DB] p-12 text-center bg-white rounded-sm">
              <div className="font-heading font-semibold text-[#0A192F]">No applications yet</div>
              <div className="text-sm text-[#4A5568] mt-1">Browse housing and request placement to start tracking.</div>
              <Link to="/search"><Button variant="outline" className="mt-4" data-testid="seeker-browse-empty"><Search className="w-4 h-4 mr-1.5" /> Browse Housing</Button></Link>
            </div>
          ) : (
            <div className="bg-white border border-[#D1D5DB] rounded-sm divide-y divide-[#D1D5DB]">
              {applications.map((a) => (
                <div key={a.id} data-testid={`app-${a.id}`} className="p-5 flex flex-wrap items-center gap-3 justify-between">
                  <div>
                    <Link to={`/listings/${a.listing_id}`} className="font-medium text-[#0A192F] hover:underline">{a.listing_name}</Link>
                    <div className="text-xs text-[#4A5568] mt-0.5">Applied {new Date(a.created_at).toLocaleDateString()}</div>
                    {a.notes && <div className="text-sm text-[#4A5568] mt-1 max-w-xl">{a.notes}</div>}
                  </div>
                  <Badge className={statusColor(a.status)}>{a.status.replace('_', ' ')}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="favorites">
          {favorites.length === 0 ? (
            <div className="border border-dashed border-[#D1D5DB] p-12 text-center bg-white rounded-sm">
              <Heart className="w-8 h-8 mx-auto text-[#4A5568] mb-3" />
              <div className="font-heading font-semibold text-[#0A192F]">No saved listings</div>
              <div className="text-sm text-[#4A5568] mt-1">Tap the heart icon on a listing to save it here.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {favorites.map((l) => (
                <div key={l.id} data-testid={`fav-${l.id}`} className="bg-white border border-[#D1D5DB] p-5 rounded-sm flex justify-between items-start">
                  <div>
                    <Link to={`/listings/${l.id}`} className="font-medium text-[#0A192F] hover:underline">{l.name}</Link>
                    <div className="text-xs text-[#4A5568] mt-0.5">{l.city}, {l.state} · {l.housing_type.replace('_',' ')}</div>
                    <div className="text-sm text-[#0A192F] mt-2">{l.available_beds}/{l.total_beds} beds available</div>
                  </div>
                  <button onClick={() => removeFav(l.id)} data-testid={`remove-fav-${l.id}`} className="p-2 hover:bg-[#F5F5F0] rounded">
                    <Trash2 className="w-4 h-4 text-[#E53E3E]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents">
          <DocumentVault
            title="Document Vault"
            description="Securely upload your ID, income proof, and supporting documents to fast-track your applications."
            testId="seeker-document-vault"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="block mb-1.5">{label}</Label>
    {children}
  </div>
);

const statusColor = (s) => {
  const m = {
    submitted: "bg-[#FEEBC8] text-[#7B341E]",
    under_review: "bg-[#BEE3F8] text-[#2A4365]",
    accepted: "bg-[#E9D8FD] text-[#44337A]",
    waitlisted: "bg-[#FAF089] text-[#744210]",
    placed: "bg-[#C6F6D5] text-[#22543D]",
    completed: "bg-[#276749] text-white",
    closed: "bg-[#1A202C] text-white",
    rejected: "bg-[#FED7D7] text-[#742A2A]",
  };
  return m[s] || "bg-[#F5F5F0] text-[#0A192F]";
};
